import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { PdfExportModal } from 'ui/components/Modal/PdfExportModal';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import ComponentSidebar from '../components/sidebar/ComponentSidebar';
import PropertiesPanel from './properties/PropertiesPanel';
import {
  normalizeTemplate,
  generatePreviewLayout,
  PreviewLayoutConfig,
  PreviewLayoutResult,
} from 'ui/modules/reports/pages/TemplatesPage/utils/templateUtils';
import DesignerCanvas from '../components/canvas/DesignerCanvas';
import DesignerHeader from './header/DesignerHeader';
import {
  CreateFormModal,
  CreateFormModalSubmitData,
} from '../../../../../components/CreateFormModal';
import {
  ARROW_KEY_MOVEMENT_STEP,
  ARROW_KEY_MOVEMENT_STEP_FAST,
  EDGE_OFFSET_BOTTOM,
  EDGE_OFFSET_RIGHT,
} from '../consts';

import {
  LabelElement,
  ElementType,
  PageSize,
  isGroupElement,
  GroupElement,
  AdvancedTableData,
  AdvancedTableHeader,
  AdvancedTableCellData,
  BaseElementProps,
  DynamicDataField,
  TemplateApiPayload,
} from '../types';
import { useHistory } from '../hooks/useHistory';
import jsPDF from 'jspdf';
import { Stage as KonvaStage } from 'konva/lib/Stage';
import { v4 as uuid } from 'uuid';
import SalesOrderSelectSidebar from './sidebar/SalesOrderSelectSidebar';
import { cloneDeep, isEqual } from 'lodash';
import { SalesOrder } from 'services/salesOrders';
import { fetchCustomFieldsAPI, ObjectType } from 'services/customFields';
import { SALES_ORDER_FIELDS } from './properties/consts';
import {
  createTemplateApi,
  getTemplatesApi,
  updateTemplateApi,
  deleteTemplateApi,
  getTemplateByIdApi,
} from 'services/templates/api';

import { showNotification } from 'services/api'; // eslint-disable-line
import { logErrorCtx } from 'app/logging';
import {
  calculateConstrainedPosition,
  calculateTranslatedElements,
} from '../helpers';

interface TemplateSnapshot {
  elements: LabelElement[];
  currentTemplate: {
    id: string;
    pages: { elements: LabelElement[] }[];
  } | null;
  currentPage: number;
  footerPage: number;
}

interface LabelDesignerProps {
  reportId?: string; // Assuming optional, adjust if necessary
}

const PAGE_SIZES: PageSize[] = [
  { name: 'Letter (8.5" x 11")', width: 816, height: 1056 },
];

const LabelDesigner = ({
  reportId = 'sales order', // Default reportId if not provided
}: LabelDesignerProps): React.ReactElement => {
  const [selectedElement, setSelectedElement] = useState<LabelElement | null>(
    null
  );
  const [selectedElements, setSelectedElements] = useState<LabelElement[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showPageBreaks, setShowPageBreaks] = useState(true);
  const [showMargins, setShowMargins] = useState(true);
  const [selectedPageSize, setSelectedPageSize] = useState<PageSize>(
    PAGE_SIZES[0]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('template-export');
  const [selectedSalesOrder, setSelectedSalesOrder] =
    useState<SalesOrder | null>(null);
  const [dynamicCustomFields, setDynamicCustomFields] = useState<
    DynamicDataField[]
  >([]);

  const [availableTemplates, setAvailableTemplates] = useState<
    TemplateApiPayload[]
  >([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = React.useState(false);

  const [isCreateFormModalOpen, setIsCreateFormModalOpen] =
    React.useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(
    null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDeleteId, setTemplateToDeleteId] = useState<string | null>(
    null
  );
  const [pageDeleteConfirmOpen, setPageDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [systemConfirmModalOpen, setSystemConfirmModalOpen] = useState(false);
  const [isSettingSystemTemplate, setIsSettingSystemTemplate] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [footerHeight, setFooterHeight] = useState(80);
  const [footerPage, setFooterPage] = useState(0);

  // Template snapshot for clean preview mode transitions
  const templateSnapshotRef = useRef<TemplateSnapshot | null>(null);

  const {
    elements,
    recordChange,
    updateElementsWithoutHistory,
    resetHistory,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory([]);

  // Calculate totalPages from the current template's pages array
  const totalPages = useMemo(() => {
    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );
    if (currentTemplate) {
      const normalizedTemplate = normalizeTemplate(currentTemplate.template);
      return normalizedTemplate.pages.length;
    }
    return 1;
  }, [availableTemplates, currentTemplateId]);

  const handleOpenCreateFormModal = () => setIsCreateFormModalOpen(true);
  const handleCloseCreateFormModal = () => setIsCreateFormModalOpen(false);

  // Create snapshot of current template state before entering preview mode
  const createTemplateSnapshot = () => {
    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );

    const noFooterElements = elements.map((element) => {
      return { ...element, footerElement: false };
    });

    const snapshot: TemplateSnapshot = {
      elements: cloneDeep(noFooterElements),
      currentTemplate: currentTemplate
        ? {
            id: currentTemplate.id,
            pages: cloneDeep(normalizeTemplate(currentTemplate.template).pages),
          }
        : null,
      currentPage,
      footerPage,
    };

    templateSnapshotRef.current = snapshot;
  };

  // Restore template state from snapshot when exiting preview mode
  const restoreFromTemplateSnapshot = (): void => {
    const snapshot = templateSnapshotRef.current;
    if (!snapshot) {
      console.warn('No template snapshot found to restore from');
      return;
    }

    // Restore elements array
    updateElementsWithoutHistory(snapshot.elements);

    // Restore template pages if we have a current template
    if (
      snapshot.currentTemplate &&
      currentTemplateId === snapshot.currentTemplate.id
    ) {
      const currentTemplate = availableTemplates.find(
        (t) => t.id === currentTemplateId
      );

      if (currentTemplate) {
        const normalizedTemplate = normalizeTemplate(currentTemplate.template);
        const restoredTemplate: TemplateApiPayload = {
          ...currentTemplate,
          template: {
            ...normalizedTemplate,
            pages: snapshot.currentTemplate.pages,
          },
        };

        // Update template in availableTemplates state
        setAvailableTemplates((prevTemplates) =>
          prevTemplates.map((t) =>
            t.id === currentTemplateId ? restoredTemplate : t
          )
        );
      }
    }

    // Restore page-related state
    setCurrentPage(snapshot.currentPage);
    setFooterPage(snapshot.footerPage);

    // Clear the snapshot to free memory
    templateSnapshotRef.current = null;
  };

  // Helper function to remove trailing empty pages from a pages array
  const removeTrailingEmptyPages = (
    pages: { elements: LabelElement[] }[]
  ): { elements: LabelElement[] }[] => {
    // Start from the end and remove pages until we hit a page with elements
    const trimmedPages = [...pages];
    while (
      trimmedPages.length > 1 &&
      trimmedPages[trimmedPages.length - 1].elements.length === 0
    ) {
      trimmedPages.pop();
    }
    return trimmedPages;
  };

  // Helper function to organize elements by page based on their Y position
  const organizeElementsByPage = (
    elements: LabelElement[]
  ): { elements: LabelElement[] }[] => {
    const pageHeight = selectedPageSize.height;
    const pages: { elements: LabelElement[] }[] = [];

    // Initialize pages array with empty pages up to totalPages
    for (let i = 0; i < totalPages; i++) {
      pages.push({ elements: [] });
    }

    // Assign each element to the correct page based on its Y position
    elements.forEach((element) => {
      const pageIndex = Math.floor(element.y / pageHeight);
      const clampedPageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));

      // Adjust element Y position to be relative to its page
      const adjustedElement = {
        ...element,
        y: element.y - clampedPageIndex * pageHeight,
        pageIndex: clampedPageIndex,
      };

      pages[clampedPageIndex].elements.push(adjustedElement);
    });

    // Remove trailing empty pages before saving
    return removeTrailingEmptyPages(pages);
  };

  const handleUpdateTemplate = async () => {
    setIsUpdatingTemplate(true);
    if (!currentTemplateId) {
      showNotification('No template selected to update.', {
        variant: 'warning',
      });
      setIsUpdatingTemplate(false); // Reset on early return
      return;
    }

    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );
    if (!currentTemplate) {
      showNotification('Selected template not found.', { variant: 'error' });
      setIsUpdatingTemplate(false); // Reset on early return
      return;
    }

    // Normalize the existing template to ensure we have the new structure
    const normalizedTemplate = normalizeTemplate(currentTemplate.template);

    // Filter out split tables (preview-only elements) before saving
    const elementsToSave = elements.filter((el) => !el.id.includes('_page_'));

    // Organize elements by page based on their Y position
    const organizedPages = organizeElementsByPage(elementsToSave);

    const payload: TemplateApiPayload = {
      ...currentTemplate, // Spreads id, name, formType, defaultTemplate
      template: {
        ...normalizedTemplate, // Use normalized values as a base
        pages: organizedPages, // Use organized pages instead of single page
        pageSize: selectedPageSize, // Use the current, selected page size
        footerEnabled: showFooter, // Save footer enabled state
        footerHeight: footerHeight, // Save footer height
      },
    };

    try {
      const updatedTemplateResponse = await updateTemplateApi(payload);
      setAvailableTemplates((prevTemplates) =>
        prevTemplates.map((t) =>
          t.id === updatedTemplateResponse.id ? updatedTemplateResponse : t
        )
      );
      showNotification('Template updated successfully!', {
        variant: 'success',
      });
    } catch (error) {
      if (error instanceof Error) {
        logErrorCtx('Failed to update template', { error });
      } else {
        logErrorCtx('Failed to update template', {
          error: new Error(String(error)),
        });
      }
      showNotification('Failed to update template.', { variant: 'error' });
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  const handleCreateFormSubmit = async (data: CreateFormModalSubmitData) => {
    setIsTemplatesLoading(true);
    try {
      let templateToCreate: TemplateApiPayload;

      if (data.startingPoint === 'template' && data.templateId) {
        const baseTemplate = availableTemplates.find(
          (t) => t.id === data.templateId
        );

        if (baseTemplate) {
          templateToCreate = {
            id: '',
            name: data.templateName,
            defaultTemplate: false,
            systemTemplate: false,
            formType: baseTemplate.formType,
            template: normalizeTemplate(baseTemplate.template), // Ensure new format
          };
        } else {
          throw new Error('Base template not found');
        }
      } else {
        // Creating from scratch
        templateToCreate = {
          id: '',
          name: data.templateName,
          defaultTemplate: false,
          systemTemplate: false,
          formType: reportId,
          template: {
            version: '2.0',
            pages: [{ elements: [] }],
            headerElements: [],
            footerElements: [],
            pageSize: selectedPageSize,
            marginTop: 0,
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0,
            footerEnabled: false,
            footerHeight: 80,
          },
        };
      }

      const newTemplate = await createTemplateApi([templateToCreate]);

      handleCloseCreateFormModal(); // Close modal ONLY on success
      showNotification('Template created successfully!', {
        variant: 'success',
      });

      // Refetch templates to include the new one and then select it
      await fetchTemplates(reportId, newTemplate.id);
    } catch (error: any) {
      let errorMessage =
        'An unexpected error occurred while creating the template.';

      // Axios-specific error structure for detailed feedback
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      logErrorCtx('Failed to create template', { error });
      showNotification(errorMessage, { variant: 'error' });
    } finally {
      setIsTemplatesLoading(false); // This will stop the button spinner
    }
  };

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);
  const stageRef = useRef<KonvaStage>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomFields = async () => {
      try {
        const customFields = (
          await fetchCustomFieldsAPI({}, ObjectType.SalesOrder)
        ).data;

        if (customFields && Array.isArray(customFields)) {
          const salesOrderCustomFields = customFields.map((field: any) => ({
            value: `customFields.${field.name}`, // e.g., customFields.Date
            label: field.name, // e.g., Date
            group: 'Custom Fields',
          }));
          if (isMounted) {
            setDynamicCustomFields(salesOrderCustomFields);
          }
        }
      } catch (error) {
        console.error('Failed to fetch custom fields:', error);
        // Handle error appropriately, e.g., show a notification to the user
      }
    };

    fetchCustomFields();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array ensures this runs once on mount

  const fetchTemplates = async (
    formType: string,
    selectTemplateId?: string
  ) => {
    setIsTemplatesLoading(true);
    try {
      const templates = await getTemplatesApi(formType);
      const verifiedTemplates = templates.filter((template) => {
        if (!template.template) return false;
        // A template is valid if it's a V1 with elements or a V2 with pages.
        const hasElements = 'elements' in template.template;
        const hasPages = 'pages' in template.template;
        return (hasElements || hasPages) && template.template.pageSize;
      });

      setAvailableTemplates(verifiedTemplates);

      let templateToSelect = null;
      if (selectTemplateId) {
        templateToSelect = verifiedTemplates.find(
          (t) => t.id === selectTemplateId
        );
      } else if (!currentTemplateId && verifiedTemplates.length > 0) {
        // Select first template if none is selected
        templateToSelect = verifiedTemplates[0];
      }

      if (templateToSelect) {
        handleTemplateSelection(templateToSelect);
      }
    } catch (error) {
      if (error instanceof Error) {
        logErrorCtx('Failed to fetch templates', { error });
      } else {
        logErrorCtx('Failed to fetch templates', {
          error: new Error(String(error)),
        });
      }
      showNotification('Failed to load templates.', { variant: 'error' });
      setAvailableTemplates([]);
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const allAvailableDynamicFields = useMemo(() => {
    // Combine static fields with dynamically fetched custom fields
    return [...SALES_ORDER_FIELDS, ...dynamicCustomFields];
  }, [dynamicCustomFields, SALES_ORDER_FIELDS]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key.toLowerCase() === 'z') {
          if (e.shiftKey) {
            if (canRedo) redo();
          } else {
            if (canUndo) undo();
          }
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  // Add this new useEffect to re-sync selectedElement after elements array changes (e.g., from undo/redo)
  useEffect(() => {
    if (selectedElement) {
      const refreshedSelectedElement = elements.find(
        (el) => el.id === selectedElement.id
      );
      if (refreshedSelectedElement) {
        if (!isEqual(selectedElement, refreshedSelectedElement)) {
          setSelectedElement(refreshedSelectedElement);
        }
      } else {
        setSelectedElement(null);
      }
    }
    if (selectedElements.length > 0) {
      const refreshedSelectedElements = elements.filter((el) =>
        selectedElements.some((sel) => sel.id === el.id)
      );
      setSelectedElements(refreshedSelectedElements);
    }
  }, [elements, selectedElement]); // Intentionally include selectedElement in deps to handle its changes too

  // Ensure currentPage doesn't exceed totalPages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Handle keyboard events for deletion and movement
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedElements.length || previewMode) return;

      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedElementsIds = selectedElements.map((el) => el.id);
        const newElements = elements.filter(
          (element) => !selectedElementsIds.includes(element.id)
        );
        recordChange(newElements);
        setSelectedElement(null);
        return;
      }

      const isArrowKey = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
      ].includes(e.key);
      if (isArrowKey) {
        e.preventDefault();
        const selectedElementToMove = selectedElements[0];

        const step = e.shiftKey
          ? ARROW_KEY_MOVEMENT_STEP_FAST
          : ARROW_KEY_MOVEMENT_STEP;
        let newX = selectedElementToMove.x;
        let newY = selectedElementToMove.y;

        switch (e.key) {
          case 'ArrowUp':
            newY -= step;
            break;
          case 'ArrowDown':
            newY += step;
            break;
          case 'ArrowLeft':
            newX -= step;
            break;
          case 'ArrowRight':
            newX += step;
            break;
        }

        const maxX =
          selectedPageSize.width -
          selectedElementToMove.width -
          EDGE_OFFSET_RIGHT;
        const constrainedX = Math.max(0, Math.min(newX, maxX));

        const canvasHeight = selectedPageSize.height * totalPages;
        let maxY =
          canvasHeight - selectedElementToMove.height - EDGE_OFFSET_BOTTOM;

        if (showFooter && selectedElementToMove.type === 'advancedTable') {
          maxY =
            canvasHeight -
            footerHeight -
            selectedElementToMove.height -
            EDGE_OFFSET_BOTTOM;
        }

        const constrainedY = Math.max(0, Math.min(newY, maxY));

        if (
          constrainedX !== selectedElementToMove.x ||
          constrainedY !== selectedElementToMove.y
        ) {
          const deltaX = constrainedX - selectedElementToMove.x;
          const deltaY = constrainedY - selectedElementToMove.y;

          const { updatedElements, updatedSelectedElements } =
            calculateTranslatedElements(
              selectedElementToMove,
              selectedElements,
              elements,
              deltaX,
              deltaY,
              selectedPageSize,
              totalPages,
              showFooter,
              footerHeight
            );

          recordChange(updatedElements);
          setSelectedElements(updatedSelectedElements);

          // If there's a primary selected element, update it as well
          if (selectedElement) {
            const updatedSelectedElement = updatedSelectedElements.find(
              (el) => el.id === selectedElement.id
            );
            if (updatedSelectedElement) {
              setSelectedElement(updatedSelectedElement);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedElement,
    selectedElements,
    elements,
    recordChange,
    previewMode,
    selectedPageSize,
    totalPages,
    showFooter,
    footerHeight,
  ]);

  const handleCopy = (e: ClipboardEvent) => {
    if (!selectedElements.length || previewMode) return;

    e.preventDefault();

    e.clipboardData?.setData(
      'application/x-fb-template-canvas',
      JSON.stringify(selectedElements)
    );
  };

  const handlePaste = (e: ClipboardEvent) => {
    if (previewMode) return;

    e.preventDefault();

    const clipboardData = e?.clipboardData;
    const pastedData = clipboardData?.getData(
      'application/x-fb-template-canvas'
    );

    if (pastedData && stageRef.current) {
      const parsedElements = JSON.parse(pastedData);
      if (Array.isArray(parsedElements) && parsedElements.length > 0) {
        const stage = stageRef.current;
        const scale = stage.scaleX();
        const pointerPosition = stage.getPointerPosition();

        if (pointerPosition) {
          const canvasWidth = selectedPageSize.width;
          const canvasHeight = selectedPageSize.height * totalPages;

          let minX = parsedElements[0].x;
          let minY = parsedElements[0].y;
          let maxX = parsedElements[0].x + parsedElements[0].width;
          let maxY = parsedElements[0].y + parsedElements[0].height;

          parsedElements.forEach((el: LabelElement) => {
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + el.width);
            maxY = Math.max(maxY, el.y + el.height);
          });

          const groupWidth = maxX - minX;
          const groupHeight = maxY - minY;

          const dropX = pointerPosition.x / scale;
          const dropY = pointerPosition.y / scale;

          const elementsContainAdvancedTable = parsedElements.some(
            (el: LabelElement) =>
              isGroupElement(el)
                ? el.children.some((child) => child.type === 'advancedTable')
                : el.type === 'advancedTable'
          );
          const footerHeightToConsider =
            elementsContainAdvancedTable && showFooter ? footerHeight : 0;

          const { finalX, finalY } = calculateConstrainedPosition(
            dropX,
            dropY,
            groupWidth,
            groupHeight,
            canvasWidth,
            canvasHeight,
            footerHeightToConsider
          );

          const offsetX = finalX - minX;
          const offsetY = finalY - minY;

          const newElements = parsedElements.map((el: LabelElement) => {
            const newX = el.x + offsetX;
            const newY = el.y + offsetY;

            const constrainedX = Math.max(
              0,
              Math.min(newX, canvasWidth - el.width)
            );
            const constrainedY = Math.max(
              0,
              Math.min(newY, canvasHeight - el.height)
            );

            return {
              ...el,
              id: uuid(),
              x: constrainedX,
              y: constrainedY,
            };
          });

          recordChange([...elements, ...newElements]);

          setSelectedElements(newElements);
          if (newElements.length === 1) {
            setSelectedElement(newElements[0]);
          }
        } else {
          const newElements = parsedElements.map((el: LabelElement) => ({
            ...el,
            id: uuid(),
          }));
          recordChange([...elements, ...newElements]);
        }
      }
    }
  };

  // Handle clipboard copy event
  useEffect(() => {
    window.addEventListener('copy', handleCopy as EventListener);
    return () =>
      window.removeEventListener('copy', handleCopy as EventListener);
  }, [handleCopy]);

  // Handle clipboard cut event
  useEffect(() => {
    const handleCut = (e: ClipboardEvent) => {
      handleCopy(e);

      const selectedElementsIds = selectedElements.map((el) => el.id);
      const newElements = elements.filter(
        (element) => !selectedElementsIds.includes(element.id)
      );
      recordChange(newElements);
      setSelectedElement(null);
    };

    window.addEventListener('cut', handleCut as EventListener);
    return () => window.removeEventListener('cut', handleCut as EventListener);
  }, [selectedElements, elements, recordChange, handleCopy]);

  // Handle clipboard paste event
  useEffect(() => {
    window.addEventListener('paste', handlePaste as EventListener);
    return () =>
      window.removeEventListener('paste', handlePaste as EventListener);
  }, [handlePaste]);

  const handleBringToFront = () => {
    if (!selectedElement) return;

    const newElements = elements.filter((el) => el.id !== selectedElement.id);
    const updatedElements = [...newElements, selectedElement];
    recordChange(updatedElements);
  };

  const handleSendToBack = () => {
    if (!selectedElement) return;

    const newElements = elements.filter((el) => el.id !== selectedElement.id);
    const updatedElements = [selectedElement, ...newElements];
    recordChange(updatedElements);
  };

  const handleImageUpload = (x: number, y: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newElement: LabelElement = {
            id: uuid(),
            type: 'image',
            x,
            y,
            width: 100,
            height: 100,
            rotation: 0,
            src: e.target?.result as string,
          };

          const newElements = [...elements, newElement];
          recordChange(newElements);
          setSelectedElement(newElement);
        };
        reader.readAsDataURL(file);
      }
    };

    input.click();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && over.id === 'canvas') {
      const type = active.id as ElementType;
      const canvasRect = document
        .getElementById('label-canvas')
        ?.getBoundingClientRect();
      const stageRect = stageRef.current?.container()?.getBoundingClientRect();

      if (canvasRect && stageRect && stageRef.current) {
        const scale = stageRef.current.scaleX();
        const stage = stageRef.current;

        const pointerPosition = stage.getPointerPosition();
        if (pointerPosition) {
          // Get canvas dimensions
          const canvasWidth = selectedPageSize.width;
          const canvasHeight = selectedPageSize.height * totalPages;

          // Calculate element dimensions dynamically based on type
          const elementWidth =
            type === 'advancedTable'
              ? canvasWidth * 0.9 // Initial width 90% of canvas
              : type === 'line'
              ? 100
              : 100;
          const elementHeight =
            type === 'advancedTable'
              ? 150 // Keep height or adjust as needed, e.g., relative to rows
              : type === 'line'
              ? 10
              : 50;

          // Determine the drop position (assuming x, y from onDrop are relative to the drop target or stage)
          const dropX = pointerPosition.x / scale;
          const dropY = pointerPosition.y / scale;

          const footerHeightToConsider =
            type === 'advancedTable' && showFooter ? footerHeight : 0;

          const { finalX, finalY } = calculateConstrainedPosition(
            dropX,
            dropY,
            elementWidth,
            elementHeight,
            canvasWidth,
            canvasHeight,
            footerHeightToConsider
          );

          let elementSpecificProps: Partial<
            Omit<LabelElement, keyof BaseElementProps>
          > = {};
          let advancedTableCalculatedHeight: number | undefined; // To store specific height for advanced table

          if (type === 'image') {
            handleImageUpload(finalX, finalY);
            return;
          }

          if (type === 'text') {
            elementSpecificProps = {
              text: 'Sample Text',
              fontSize: 16,
              fontFamily: 'Arial',
              fill: '#000000',
              align: 'left',
              backgroundColor: 'transparent',
            };
          } else if (type === 'box') {
            elementSpecificProps = {
              fill: 'transparent',
              stroke: '#000000',
              lineWidth: 1,
            };
          } else if (type === 'line') {
            elementSpecificProps = {
              stroke: '#000000',
              lineWidth: 1,
            };
          } else if (type === 'advancedTable') {
            // Constants for height calculation
            const initialHeaderRowHeight = 30;
            const initialDefaultCellFontSize = 13;
            const initialDefaultNumDataRows = 1;
            const DATA_ROW_PADDING = 8; // Set padding to 0
            const defaultDataRowHeight =
              initialDefaultCellFontSize + DATA_ROW_PADDING;

            // Recalculate elementHeight specifically for advancedTable based on new row height logic
            // The elementHeight variable was previously set based on a generic value for advancedTable.
            // We override it here.
            const newElementHeight =
              initialHeaderRowHeight +
              initialDefaultNumDataRows * defaultDataRowHeight;
            // Update the general elementHeight that will be used for the newElement creation
            // This is a bit tricky as elementHeight is declared outside this block. For clarity,
            // we'll use newElementHeight for calculations within this block and ensure the final
            // newElement uses this adjusted height.

            const defaultHeaders: AdvancedTableHeader[] = [
              { key: 'name', label: 'Name' },
              { key: 'description', label: 'Description' },
              { key: 'salesOrderItemType', label: 'Type' },
              { key: 'quantity', label: 'Qty' },
              { key: 'price', label: 'Price' },
              { key: 'discount', label: 'Discount' },
              { key: 'total', label: 'Total' },
            ];

            const defaultNumColumns = defaultHeaders.length;

            const defaultColumnWidths = Array(defaultNumColumns).fill(
              elementWidth / defaultNumColumns // elementWidth is from the outer scope
            );
            // Use the calculated defaultDataRowHeight for each data row
            const defaultRowHeights = Array(initialDefaultNumDataRows).fill(
              defaultDataRowHeight
            );

            const defaultTableData: AdvancedTableCellData[][] = Array(
              initialDefaultNumDataRows
            )
              .fill(null)
              .map(() =>
                Array(defaultNumColumns)
                  .fill(null)
                  .map(() => ({ content: `` }))
              );

            const advancedTableData: AdvancedTableData = {
              numRows: initialDefaultNumDataRows,
              numColumns: defaultNumColumns,
              columnWidths: defaultColumnWidths,
              rowHeights: defaultRowHeights,
              data: defaultTableData,
              headerRow: true,
              headerRowHeight: initialHeaderRowHeight,
              headers: defaultHeaders,
              headerFontFamily: 'Arial',
              headerFontSize: 15,
              headerBackgroundColor: '#f0f0f0',
              headerTextColor: '#000000',
              defaultCellFontFamily: 'Arial',
              defaultCellFontSize: initialDefaultCellFontSize,
              defaultCellBackgroundColor: '#ffffff',
              defaultCellTextColor: '#000000',
              borderColor: '#cccccc',
              borderWidth: 1,
              showGrid: true,
              autoRowHeight: true, // Default to auto-sizing rows
              highlightColor: '#007bff',
            };
            elementSpecificProps = {
              advancedTableData,
            };
            // Ensure the newElement uses the recalculated height for advancedTable
            // This is a direct assignment to the variable that will be used later.
            // This assumes elementHeight was declared with 'let' in the outer scope.
            // If it was 'const', this approach needs rethinking, but based on typical patterns, it's 'let'.
            // To be safer, we'll ensure the newElement below uses this specific height.
            advancedTableCalculatedHeight = newElementHeight; // Store the calculated height
          }

          const newElement: LabelElement = {
            id: uuid(),
            type,
            x: finalX,
            y: finalY,
            width: elementWidth,
            height:
              type === 'advancedTable' &&
              advancedTableCalculatedHeight !== undefined
                ? advancedTableCalculatedHeight
                : elementHeight,
            rotation: 0,
            draggable: true,
            ...elementSpecificProps,
          };

          const newElements = [...elements, newElement];
          recordChange(newElements);
          setSelectedElement(newElement);
        }
      }
    }

    setIsDragging(false);
  };

  const openPdfExportDialog = () => {
    setPdfFilename('template-export'); // or derive from template name
    setPdfExportDialogOpen(true);
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleExportPDF = async (filename: string) => {
    if (!stageRef.current) return;
    const stage = stageRef.current;

    // Store original state
    const gridVisible = showGrid;
    const pageBreaksVisible = showPageBreaks;
    const marginsVisible = showMargins;
    const originalScale = stage.scale();
    const originalPosition = stage.position();

    // Prepare for export
    if (gridVisible) setShowGrid(false);
    if (pageBreaksVisible) setShowPageBreaks(false);
    if (marginsVisible) setShowMargins(false);
    setSelectedElement(null);
    setSelectedElements([]);

    // Allow Konva to re-render before exporting
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: [selectedPageSize.width, selectedPageSize.height],
      });

      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.draw();

      // Add 0.5 inch margins for printing
      const MARGIN_INCHES = 0.5;
      const MARGIN_PX = MARGIN_INCHES * 72; // Convert inches to points (72 points per inch)

      // Calculate content dimensions with margins
      const contentWidth = selectedPageSize.width - 2 * MARGIN_PX;
      const contentHeight = selectedPageSize.height - 2 * MARGIN_PX;

      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        // During export, margins are hidden so no offset is needed
        const dataUrl = stage.toDataURL({
          x: 0,
          y: i * selectedPageSize.height,
          width: selectedPageSize.width,
          height: selectedPageSize.height,
          pixelRatio: 2, // Increase for better quality
        });

        // Add image with margins - centered on page
        pdf.addImage(
          dataUrl,
          'PNG',
          MARGIN_PX, // X offset for left margin
          MARGIN_PX, // Y offset for top margin
          contentWidth, // Scaled width (with margins)
          contentHeight // Scaled height (with margins)
        );
      }

      pdf.save(`${filename}.pdf`);
    } catch (error) {
      logErrorCtx('Error exporting PDF', { error: error as Error });
      showNotification('Failed to export PDF. Please try again.', {
        variant: 'error',
      });
    } finally {
      // Restore original stage state
      stage.scale(originalScale);
      stage.position(originalPosition);
      stage.draw();

      if (gridVisible) setShowGrid(true);
      if (pageBreaksVisible) setShowPageBreaks(true);
      if (marginsVisible) setShowMargins(true);

      setPdfExportDialogOpen(false);
    }
  };

  const handleTogglePreview = () => {
    const wasInPreviewMode = previewMode;

    if (wasInPreviewMode) {
      // Exiting preview mode - restore from snapshot
      restoreFromTemplateSnapshot();
      setSelectedElement(null);
      setSelectedElements([]);
      setSelectedSalesOrder(null);
      resetHistory();
    } else {
      createTemplateSnapshot();
    }
    setPreviewMode(!previewMode);
  };

  const handleToggleGrid = () => {
    setShowGrid(!showGrid);
  };

  const handleToggleMargins = () => {
    setShowMargins(!showMargins);
  };

  const handleAddPage = () => {
    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );

    if (currentTemplate) {
      const normalizedTemplate = normalizeTemplate(currentTemplate.template);
      const newPage = { elements: [] };

      const updatedTemplate: TemplateApiPayload = {
        ...currentTemplate,
        template: {
          ...normalizedTemplate,
          pages: [...normalizedTemplate.pages, newPage],
        },
      };

      // Update the template in state
      const updatedTemplates = availableTemplates.map((t) =>
        t.id === currentTemplateId ? updatedTemplate : t
      );
      setAvailableTemplates(updatedTemplates);

      // Only record the change for undo/redo in design mode
      recordChange(elements);

      // Switch to the new page
      setCurrentPage(totalPages + 1);

      // In design mode, move footer to the new last page (but keep footer elements in place)
      if (!previewMode && showFooter) {
        const newTotalPages = totalPages + 1;
        setFooterPage(newTotalPages - 1); // Footer page is zero-based, so last page is totalPages - 1
      }
    }
  };

  const performPageRemoval = () => {
    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );

    if (currentTemplate && totalPages > 1) {
      const normalizedTemplate = normalizeTemplate(currentTemplate.template);

      // Calculate which page we're removing (the last page)
      const pageToRemove = totalPages - 1;
      const pageHeight = selectedPageSize.height;
      const pageStartY = pageToRemove * pageHeight;
      const pageEndY = (pageToRemove + 1) * pageHeight;

      // Remove elements that are positioned on the page being deleted
      const elementsToKeep = elements.filter((element) => {
        return element.y < pageStartY || element.y >= pageEndY;
      });

      // Update elements in the canvas
      recordChange(elementsToKeep);

      // Remove the last page (simplest approach)
      const updatedPages = normalizedTemplate.pages.slice(0, -1);

      const updatedTemplate: TemplateApiPayload = {
        ...currentTemplate,
        template: {
          ...normalizedTemplate,
          pages: updatedPages,
        },
      };

      // Update the template in state
      const updatedTemplates = availableTemplates.map((t) =>
        t.id === currentTemplateId ? updatedTemplate : t
      );
      setAvailableTemplates(updatedTemplates);

      // Adjust current page if necessary
      if (currentPage > updatedPages.length) {
        setCurrentPage(updatedPages.length);
      }

      // Clear selection if selected elements were on the removed page
      if (
        selectedElement &&
        selectedElement.y >= pageStartY &&
        selectedElement.y < pageEndY
      ) {
        setSelectedElement(null);
      }

      const selectedElementsToKeep = selectedElements.filter((element) => {
        return element.y < pageStartY || element.y >= pageEndY;
      });
      setSelectedElements(selectedElementsToKeep);
    }
  };

  const handleRemovePage = () => {
    const currentTemplate = availableTemplates.find(
      (t) => t.id === currentTemplateId
    );

    if (currentTemplate && totalPages > 1) {
      const normalizedTemplate = normalizeTemplate(currentTemplate.template);

      // Don't allow removing the last page
      if (normalizedTemplate.pages.length <= 1) {
        showNotification('Cannot remove the last page', { variant: 'warning' });
        return;
      }

      // Calculate which page we're removing (the last page)
      const pageToRemove = totalPages - 1;
      const pageHeight = selectedPageSize.height;
      const pageStartY = pageToRemove * pageHeight;
      const pageEndY = (pageToRemove + 1) * pageHeight;

      // Check if there are elements on the page being deleted
      const elementsOnPage = elements.filter((element) => {
        return element.y >= pageStartY && element.y < pageEndY;
      });

      if (elementsOnPage.length > 0) {
        // Show confirmation dialog if there are elements on the page
        setPageDeleteConfirmOpen(true);
      } else {
        // No elements on the page, proceed with deletion
        performPageRemoval();
      }
    }
  };

  const handleConfirmPageDelete = () => {
    setPageDeleteConfirmOpen(false);
    performPageRemoval();
  };

  const handleCancelPageDelete = () => {
    setPageDeleteConfirmOpen(false);
  };

  const handleUpdateMultipleElements = (updatedElements: LabelElement[]) => {
    const updatedElementsMap = new Map(
      updatedElements.map((element) => [element.id, element])
    );
    const newElements = elements.map((el) =>
      updatedElementsMap.has(el.id) ? updatedElementsMap.get(el.id)! : el
    );

    recordChange(newElements);

    if (selectedElement) {
      const updatedSelectedElement = updatedElementsMap.get(selectedElement.id);
      if (updatedSelectedElement) {
        setSelectedElement(updatedSelectedElement);
      }
    }

    setSelectedElements(updatedElements);
  };

  const handleUpdateElement = (
    id: string,
    updates: Partial<LabelElement>,
    updateType?: string
  ) => {
    const newElements = elements.map((el) =>
      el.id === id ? { ...el, ...updates } : el
    );
    recordChange(newElements);
    setSelectedElement((prevSelected) =>
      prevSelected && prevSelected.id === id
        ? { ...prevSelected, ...updates }
        : prevSelected
    );

    setSelectedElements((prevSelectedElements) =>
      prevSelectedElements.map((el) =>
        el.id === id ? { ...el, ...updates } : el
      )
    );

    if (
      selectedElement &&
      selectedElement.type === 'advancedTable' &&
      (updateType === 'width' || updateType === 'height')
    ) {
      const tableElement = selectedElement as any;
      const newTableData = { ...tableElement.advancedTableData };
      if (updateType === 'width' && updates.width !== undefined) {
        newTableData.columnWidths = newTableData.columnWidths.map(
          () => (updates.width as number) / newTableData.numColumns
        );
      }
      if (updateType === 'height' && updates.height !== undefined) {
        newTableData.rowHeights = newTableData.rowHeights.map(
          () => (updates.height as number) / newTableData.numRows
        );
      }
      const newElement = {
        ...selectedElement,
        advancedTableData: newTableData,
      };
      const newElements = elements.map((el) =>
        el.id === id ? newElement : el
      );
      recordChange(newElements);
      setSelectedElement(newElement);
    }
  };

  const handleDuplicateElement = (id: string): LabelElement | undefined => {
    const elementToDuplicate = elements.find((element) => element.id === id);

    if (elementToDuplicate) {
      if (isGroupElement(elementToDuplicate)) {
        const duplicatedGroup: GroupElement = {
          ...elementToDuplicate,
          id: uuid(),
          x: elementToDuplicate.x + 20,
          y: elementToDuplicate.y + 20,
          children: elementToDuplicate.children.map((child) => ({
            ...child,
            id: uuid(),
          })),
        };
        const newElements = [...elements, duplicatedGroup];
        recordChange(newElements);
        return duplicatedGroup;
      }
      const duplicatedElement: LabelElement = {
        ...elementToDuplicate,
        id: uuid(),
        x: elementToDuplicate.x + 20,
        y: elementToDuplicate.y + 20,
      };

      const newElements = [...elements, duplicatedElement];
      recordChange(newElements);
      return duplicatedElement;
    }

    return undefined;
  };

  const handleClearCanvas = () => {
    recordChange([]);
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleGroup = () => {
    if (selectedElements.length > 1) {
      let allElements: LabelElement[] = [];

      selectedElements.forEach((element) => {
        if (isGroupElement(element)) {
          const groupChildren = element.children.map((child) => ({
            ...child,
            x: child.x + element.x,
            y: child.y + element.y,
          }));
          allElements = [...allElements, ...groupChildren];
        } else {
          allElements.push(element);
        }
      });

      const minX = Math.min(...allElements.map((el) => el.x));
      const minY = Math.min(...allElements.map((el) => el.y));
      const maxX = Math.max(...allElements.map((el) => el.x + el.width));
      const maxY = Math.max(...allElements.map((el) => el.y + el.height));
      const width = maxX - minX;
      const height = maxY - minY;

      const adjustedElements = allElements.map((el) => ({
        ...el,
        id: uuid(),
        x: el.x - minX,
        y: el.y - minY,
      }));

      const groupElement: GroupElement = {
        id: uuid(),
        type: 'group',
        children: adjustedElements,
        x: minX,
        y: minY,
        width,
        height,
        rotation: 0,
      };

      const selectedIds = new Set(selectedElements.map((el) => el.id));
      const newElements = elements.filter((el) => !selectedIds.has(el.id));

      recordChange([...newElements, groupElement]);
      setSelectedElement(groupElement);
      setSelectedElements([]);
    }
  };

  const handleUngroup = () => {
    if (selectedElement && isGroupElement(selectedElement)) {
      const currentGroup = elements.find(
        (element) => element.id === selectedElement.id
      ) as GroupElement | undefined;

      if (!currentGroup) return;

      const newElements = elements.filter(
        (element) => element.id !== currentGroup.id
      );

      const restoredChildren = currentGroup.children.map((child) => {
        const groupRotationRad = ((currentGroup.rotation || 0) * Math.PI) / 180;
        const cosAngle = Math.cos(groupRotationRad);
        const sinAngle = Math.sin(groupRotationRad);

        const relativeX = child.x;
        const relativeY = child.y;
        const rotatedX = relativeX * cosAngle - relativeY * sinAngle;
        const rotatedY = relativeX * sinAngle + relativeY * cosAngle;

        return {
          ...child,
          id: uuid(),
          x: rotatedX + currentGroup.x,
          y: rotatedY + currentGroup.y,
          rotation: (child.rotation || 0) + (currentGroup.rotation || 0),
        };
      });

      recordChange([...newElements, ...restoredChildren]);
      setSelectedElement(null);
      setSelectedElements([]);
    }
  };

  // Helper function to reconstruct elements from multi-page template with absolute Y positions
  const reconstructElementsFromPages = (
    pages: { elements: LabelElement[] }[]
  ): LabelElement[] => {
    const pageHeight = selectedPageSize.height;
    const allElements: LabelElement[] = [];

    pages.forEach((page, pageIndex) => {
      page.elements.forEach((element) => {
        // Convert relative Y position back to absolute Y position
        const absoluteElement = {
          ...element,
          y: element.y + pageIndex * pageHeight,
          pageIndex: pageIndex,
        };
        allElements.push(absoluteElement);
      });
    });

    return allElements;
  };

  const handleTemplateSelection = (
    selectedTemplate: TemplateApiPayload | null
  ) => {
    if (selectedTemplate) {
      const normalizedTemplate = normalizeTemplate(selectedTemplate.template);

      // Reconstruct all elements from all pages with absolute Y positions
      const allElements = reconstructElementsFromPages(
        normalizedTemplate.pages
      );

      recordChange(allElements);
      setSelectedPageSize(selectedTemplate.template.pageSize);
      setCurrentTemplateId(selectedTemplate.id);
      setSelectedElement(null);
      setSelectedElements([]);

      // Load footer settings from template
      setShowFooter(normalizedTemplate.footerEnabled || false);
      setFooterHeight(normalizedTemplate.footerHeight || 80);
      // Compute last page index from template pages, defaulting to 0 if no pages
      const lastPageIndex =
        normalizedTemplate.pages && normalizedTemplate.pages.length > 0
          ? Math.max(0, normalizedTemplate.pages.length - 1)
          : 0;
      setFooterPage(lastPageIndex);
    } else {
      recordChange([]); // Clear elements
      setSelectedPageSize(PAGE_SIZES[0]); // Reset to default page size
      setCurrentTemplateId(null);
      setSelectedElement(null);
      setSelectedElements([]);
      setShowFooter(false);
      setFooterHeight(80);
    }
  };

  const handleDeleteTemplateInitiate = (templateId: string) => {
    setTemplateToDeleteId(templateId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTemplateConfirm = async () => {
    setIsDeleting(true);
    if (!templateToDeleteId) return;

    try {
      await deleteTemplateApi(templateToDeleteId);
      showNotification('Template deleted successfully.', {
        variant: 'success',
      });

      const newAvailableTemplates = availableTemplates.filter(
        (t) => t.id !== templateToDeleteId
      );
      setAvailableTemplates(newAvailableTemplates);

      if (
        templateToDeleteId === currentTemplateId ||
        (currentTemplateId && newAvailableTemplates.length === 0)
      ) {
        let nextTemplateToSelect: TemplateApiPayload | null = null;
        if (newAvailableTemplates.length > 0) {
          nextTemplateToSelect =
            newAvailableTemplates.find((t) => t.defaultTemplate) || // Prefer default
            newAvailableTemplates[0]; // Fallback to first
        }
        handleTemplateSelection(nextTemplateToSelect); // This will set currentTemplateId and load data
      }
      // Optionally, call fetchTemplates(reportId) again if server-side filtering/pagination is complex
      // For now, optimistic update of availableTemplates is fine.
    } catch (error) {
      logErrorCtx(
        `Failed to delete template in LabelDesigner. Template ID: ${templateToDeleteId}`,
        { error: error instanceof Error ? error : new Error(String(error)) }
      );
      showNotification('Failed to delete template.', { variant: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setTemplateToDeleteId(null);
      setIsDeleting(false);
    }
  };

  const handleSetAsDefault = async (templateId: string) => {
    try {
      // 1. Fetch the full template
      const currentTemplate = await getTemplateByIdApi(templateId);

      // 2. Create the updated payload, ensuring all fields are present
      const updatedTemplatePayload: TemplateApiPayload = {
        ...currentTemplate,
        defaultTemplate: true,
      };

      // 3. Call the existing updateTemplateApi
      await updateTemplateApi(updatedTemplatePayload);

      showNotification('Template set as default.', { variant: 'success' });
      // Re-fetch templates to update the default status across all templates
      // and ensure the local state reflects the change made by potentially another user.
      fetchTemplates(reportId);
    } catch (error) {
      logErrorCtx(
        `Failed to set template as default. Template ID: ${templateId}`,
        { error: error instanceof Error ? error : new Error(String(error)) }
      );
      showNotification('Failed to set template as default.', {
        variant: 'error',
      });
    }
  };

  const handleToggleSystemSettingConfirm = async () => {
    setIsSettingSystemTemplate(true);
    try {
      if (currentTemplateId) {
        const currentTemplate = await getTemplateByIdApi(currentTemplateId);

        const updatedTemplatePayload: TemplateApiPayload = {
          ...currentTemplate,
          systemTemplate: !currentTemplate.systemTemplate,
        };

        await updateTemplateApi(updatedTemplatePayload);
      }

      fetchTemplates(reportId);
    } catch (error) {
      logErrorCtx(
        `Failed to set template as system. Template ID: ${currentTemplateId}`,
        { error: error instanceof Error ? error : new Error(String(error)) }
      );
      showNotification('Failed to set template as system.', {
        variant: 'error',
      });
    } finally {
      setIsSettingSystemTemplate(false);
      setSystemConfirmModalOpen(false);
    }
  };

  const handleToggleFooter = () => {
    showFooter ? setFooterPage(0) : setFooterPage(totalPages - 1);
    setShowFooter(!showFooter);
  };

  useEffect(() => {
    // here
    setSelectedElement(null);
    setSelectedElements([]);
    resetHistory();

    // Generate the complete preview layout using the new consolidated function
    if (selectedSalesOrder && previewMode) {
      // Get the original template pages from snapshot to avoid race conditions
      const snapshot = templateSnapshotRef.current;
      const originalTotalPages = snapshot?.currentTemplate?.pages.length || 1;

      const config: PreviewLayoutConfig = {
        pageSize: selectedPageSize,
        showFooter,
        footerHeight,
        initialTotalPages: originalTotalPages,
      };

      const layoutResult: PreviewLayoutResult = generatePreviewLayout(
        snapshot?.elements || elements,
        selectedSalesOrder,
        config
      );

      // Update the elements with the generated preview layout
      updateElementsWithoutHistory(layoutResult.elements);

      // Update page-related state from the layout result
      setFooterPage(layoutResult.footerPage);

      // Update available templates to reflect the new total pages if needed
      if (layoutResult.totalPages !== totalPages && currentTemplateId) {
        const currentTemplate = availableTemplates.find(
          (t) => t.id === currentTemplateId
        );
        if (currentTemplate) {
          const normalizedTemplate = normalizeTemplate(
            currentTemplate.template
          );

          // Adjust pages to match the layout result
          const updatedPages = [...normalizedTemplate.pages];

          // Add pages if we need more
          while (updatedPages.length < layoutResult.totalPages) {
            updatedPages.push({ elements: [] });
          }

          // Remove pages if we need fewer (but keep at least 1 page)
          while (
            updatedPages.length > layoutResult.totalPages &&
            updatedPages.length > 1
          ) {
            updatedPages.pop();
          }

          const updatedTemplate = {
            ...currentTemplate,
            template: {
              ...normalizedTemplate,
              pages: updatedPages,
            },
          };

          setAvailableTemplates((prev) =>
            prev.map((t) => (t.id === currentTemplateId ? updatedTemplate : t))
          );
        }
      }
    }
  }, [
    selectedSalesOrder,
    previewMode,
    showFooter,
    footerHeight,
    selectedPageSize,
    currentTemplateId,
  ]);

  useEffect(() => {
    setFooterPage(totalPages - 1);
  }, [totalPages]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <DesignerHeader
          onTogglePreview={handleTogglePreview}
          previewMode={previewMode}
          showGrid={showGrid}
          onToggleGrid={handleToggleGrid}
          showMargins={showMargins}
          onToggleMargins={handleToggleMargins}
          footer={showFooter}
          onToggleFooter={handleToggleFooter}
          selectedPageSize={selectedPageSize}
          onPageSizeChange={setSelectedPageSize}
          pageSizes={PAGE_SIZES}
          onUndo={canUndo ? undo : undefined}
          onRedo={canRedo ? redo : undefined}
          onClearCanvas={handleClearCanvas}
          onGroup={handleGroup}
          selectedElements={selectedElements}
          availableTemplates={availableTemplates}
          onTemplateSelect={handleTemplateSelection}
          currentTemplateId={currentTemplateId}
          onCreateNewTemplate={handleOpenCreateFormModal}
          hasTemplateSelected={!!currentTemplateId}
          onUpdateTemplate={handleUpdateTemplate}
          isUpdatingTemplate={isUpdatingTemplate}
          onDeleteTemplate={handleDeleteTemplateInitiate}
          onSetAsDefault={handleSetAsDefault}
          onToggleSystemSetting={() => setSystemConfirmModalOpen(true)}
          isCurrentTemplateDefault={
            availableTemplates.find((t) => t.id === currentTemplateId)
              ?.defaultTemplate || false
          }
          isCurrentTemplateSystem={
            availableTemplates.find((t) => t.id === currentTemplateId)
              ?.systemTemplate || false
          }
        />

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {previewMode ? (
            <SalesOrderSelectSidebar
              selectedSalesOrder={selectedSalesOrder}
              setSelectedSalesOrder={setSelectedSalesOrder}
              onExportPDF={openPdfExportDialog}
            />
          ) : (
            <ComponentSidebar />
          )}

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              bgcolor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <DesignerCanvas
              stageRef={stageRef}
              elements={elements}
              selectedElement={selectedElement}
              setSelectedElement={setSelectedElement}
              selectedElements={selectedElements}
              setSelectedElements={setSelectedElements}
              onUpdateElement={handleUpdateElement}
              isDragging={isDragging}
              previewMode={previewMode}
              previewData={selectedSalesOrder}
              showGrid={showGrid}
              showPageBreaks={showPageBreaks}
              showMargins={showMargins}
              canvasWidth={selectedPageSize.width}
              canvasHeight={selectedPageSize.height * totalPages}
              totalPages={totalPages}
              onAddPage={handleAddPage}
              onRemovePage={handleRemovePage}
              showFooter={showFooter}
              footerHeight={footerHeight}
              footerPage={footerPage}
              onFooterHeightChange={setFooterHeight}
            />
          </Box>

          {!previewMode && (
            <PropertiesPanel
              selectedElements={selectedElements}
              canvasWidth={selectedPageSize.width}
              onUpdateElement={handleUpdateElement}
              onUpdateMultipleElements={handleUpdateMultipleElements}
              onDeleteElement={() => {
                if (selectedElement) {
                  const newElements = elements.filter(
                    (element) => element.id !== selectedElement.id
                  );
                  recordChange(newElements);
                  setSelectedElement(null);
                }
              }}
              onDuplicateElement={
                selectedElement
                  ? () => {
                      const newElement = handleDuplicateElement(
                        selectedElement.id
                      );
                      if (newElement) {
                        setSelectedElement(newElement);
                      }
                    }
                  : undefined
              }
              onBringToFront={handleBringToFront}
              onSendToBack={handleSendToBack}
              onUngroup={handleUngroup}
              availableFields={allAvailableDynamicFields}
            />
          )}
        </Box>
      </DndContext>
      {/* PDF Export Dialog */}
      <PdfExportModal
        open={pdfExportDialogOpen}
        title="Export PDF"
        initialFilename={pdfFilename}
        isLoading={false}
        onConfirm={(filenameFromModal) => {
          handleExportPDF(filenameFromModal);
          setPdfExportDialogOpen(false);
        }}
        onClose={() => setPdfExportDialogOpen(false)}
      />
      <CreateFormModal
        open={isCreateFormModalOpen}
        templates={availableTemplates.map((t) => ({
          id: t.id,
          name: t.name,
        }))}
        isLoadingTemplates={isTemplatesLoading}
        onClose={handleCloseCreateFormModal}
        onSubmit={handleCreateFormSubmit}
        defaultTemplateName="New Label Template"
      />
      <ConfirmationModal
        open={deleteConfirmOpen}
        title="Delete Template"
        body={`Are you sure you want to delete this template? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirmClicked={handleDeleteTemplateConfirm}
        onCancelClicked={() => {
          setDeleteConfirmOpen(false);
          setTemplateToDeleteId(null);
          setIsDeleting(false);
        }}
        dataQa="delete-template-confirmation-modal"
        isLoading={isDeleting}
        confirmButtonRed={true}
      />
      <ConfirmationModal
        open={pageDeleteConfirmOpen}
        title="Delete Page"
        body={`This page contains elements. Are you sure you want to delete this page and all elements on it? This action cannot be undone.`}
        confirmLabel="Delete Page"
        cancelLabel="Cancel"
        onConfirmClicked={handleConfirmPageDelete}
        onCancelClicked={handleCancelPageDelete}
        dataQa="delete-page-confirmation-modal"
        confirmButtonRed={true}
      />
      <ConfirmationModal
        open={systemConfirmModalOpen}
        title="WARNING"
        body={`You are making changes to the list of system templates. These templates are visible to all users. Please confirm.`}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onConfirmClicked={handleToggleSystemSettingConfirm}
        onCancelClicked={() => setSystemConfirmModalOpen(false)}
        dataQa="set-template-as-system-confirmation-modal"
        isLoading={isSettingSystemTemplate}
        titleRed
        confirmButtonRed
      />
    </Box>
  );
};

export default LabelDesigner;
